import { useState } from 'react';
import { Link } from 'react-router-dom';
import { radioStations } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';

export default function MasTab() {
  const { currentStation, isPlaying, stop } = useAudioPlayer();
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [theme, setTheme] = useState('system');

  const today = new Date();
  const hours = today.getHours();
  const greeting = hours < 12 ? 'Buenos días' : hours < 18 ? 'Buenas tardes' : 'Buenas noches';

  return (
    <div className="flex flex-col h-full bg-background-50">
      {/* Header */}
      <div className="safe-top px-4 pt-4 pb-3">
        <h1 className="text-2xl font-bold text-foreground-950">Más</h1>
        <p className="text-foreground-500 text-sm mt-0.5">{greeting}, explorador de ondas</p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-24 scrollbar-none space-y-4">
        {/* Now Playing Card */}
        {currentStation && (
          <div className="bg-background-100 rounded-2xl p-4">
            <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-3">
              Reproduciendo ahora
            </p>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-background-200 shrink-0">
                <img
                  src={currentStation.logoUrl}
                  alt={currentStation.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-foreground-950 font-semibold text-sm truncate">
                  {currentStation.name}
                </h3>
                <p className="text-foreground-500 text-xs">{currentStation.frequency}</p>
                {currentStation.isLive && isPlaying && (
                  <span className="inline-flex items-center gap-1 text-accent-600 text-[10px] font-bold mt-1">
                    <span className="w-1 h-1 bg-accent-500 rounded-full animate-pulse"></span>
                    EN VIVO
                  </span>
                )}
              </div>
              <button
                onClick={stop}
                className="w-9 h-9 rounded-full bg-foreground-100 flex items-center justify-center cursor-pointer hover:bg-foreground-200 transition-colors"
              >
                <i className="ri-close-line text-foreground-600"></i>
              </button>
            </div>
          </div>
        )}

        {/* Favorites radios */}
        <div>
          <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-3 px-1">
            Tus favoritas
          </p>
          <div className="flex gap-2 overflow-x-auto scrollbar-none">
            {radioStations.slice(0, 6).map((s) => (
              <Link
                key={s.id}
                to={`/radio/${s.slug}`}
                className="flex flex-col items-center gap-1.5 shrink-0 cursor-pointer group"
              >
                <div className="w-14 h-14 rounded-full overflow-hidden bg-background-200 group-hover:scale-105 transition-transform">
                  <img src={s.logoUrl} alt={s.name} className="w-full h-full object-cover" />
                </div>
                <span className="text-[10px] text-foreground-600 font-medium text-center max-w-[70px] truncate">
                  {s.name.split(' ')[0]}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Menu items */}
        <div className="bg-background-100 rounded-2xl overflow-hidden">
          {[
            { icon: 'ri-heart-3-line', label: 'Radios favoritas', href: '#' },
            { icon: 'ri-history-line', label: 'Historial de escucha', href: '#' },
            { icon: 'ri-download-2-line', label: 'Descargas offline', href: '#' },
            { icon: 'ri-notification-3-line', label: 'Notificaciones', href: '#' },
            { icon: 'ri-timer-line', label: 'Temporizador', href: '#' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => e.preventDefault()}
              className="flex items-center gap-3 px-4 py-3.5 hover:bg-background-200/50 transition-colors cursor-pointer border-b border-background-200 last:border-0"
            >
              <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center">
                <i className={`${item.icon} text-foreground-600 text-sm`}></i>
              </div>
              <span className="text-foreground-800 text-sm font-medium flex-1">{item.label}</span>
              <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            </a>
          ))}
        </div>

        {/* Theme */}
        <div className="bg-background-100 rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowThemePicker(!showThemePicker)}
            className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-background-200/50 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center">
              <i className="ri-sun-line text-foreground-600 text-sm"></i>
            </div>
            <span className="text-foreground-800 text-sm font-medium flex-1">Tema</span>
            <span className="text-foreground-400 text-xs mr-1">
              {theme === 'light' ? 'Claro' : theme === 'dark' ? 'Oscuro' : 'Sistema'}
            </span>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
          </button>
          {showThemePicker && (
            <div className="px-4 pb-4 flex gap-2">
              {[
                { key: 'light', label: 'Claro', icon: 'ri-sun-line' },
                { key: 'dark', label: 'Oscuro', icon: 'ri-moon-line' },
                { key: 'system', label: 'Sistema', icon: 'ri-smartphone-line' },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => {
                    setTheme(t.key);
                    setShowThemePicker(false);
                  }}
                  className={`flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all cursor-pointer ${
                    theme === t.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  <i className={`${t.icon} text-lg`}></i>
                  <span className="text-[10px] font-semibold">{t.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="bg-background-100 rounded-2xl overflow-hidden">
          {[
            { icon: 'ri-information-line', label: 'Acerca de', href: '#' },
            { icon: 'ri-question-line', label: 'Ayuda', href: '#' },
            { icon: 'ri-shield-check-line', label: 'Privacidad', href: '#' },
            { icon: 'ri-file-text-line', label: 'Terminos', href: '#' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={(e) => e.preventDefault()}
              className="flex items-center gap-3 px-4 py-3.5 hover:bg-background-200/50 transition-colors cursor-pointer border-b border-background-200 last:border-0"
            >
              <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center">
                <i className={`${item.icon} text-foreground-600 text-sm`}></i>
              </div>
              <span className="text-foreground-800 text-sm font-medium flex-1">{item.label}</span>
              <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
            </a>
          ))}
        </div>

        {/* Version */}
        <p className="text-center text-foreground-400 text-[10px] pb-4">
          RadioFrecuenciaWeb v1.0 · Hecho con amor para las ondas
        </p>
      </div>
    </div>
  );
}